import React, { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { getCategories, INCOME_CATEGORIES } from "./categories";

const MES_NOMES_MODAL = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
import { useAppData } from "./store";
import { Mic, MicOff, Save, X } from "lucide-react";
import { toast } from "sonner";

const TIPOS = [
  { id: "entrada",  label: "Entrada",          on: "bg-emerald-500/20 border-emerald-500/50 text-emerald-300" },
  { id: "fixo",     label: "Gasto Fixo",       on: "bg-blue-500/20 border-blue-500/50 text-blue-300" },
  { id: "variavel", label: "Gasto Variável",   on: "bg-rose-500/20 border-rose-500/50 text-rose-300" },
];

const PAGAMENTOS = ["Pix", "Débito", "Cartão", "Dinheiro", "Boleto", "Transferência", "Corretora", "Outro"];

const today = () => new Date().toISOString().slice(0, 10);

const buildEmpty = (tipo) => ({
  desc: "", valor: "", data: today(),
  cat: tipo === "entrada" ? "salario" : "mercado",
  pagamento: "Pix", repetir: false, pago: false, repetirAte: "", nota: "",
});

/* ---- Web Speech API quick voice-to-text ---- */
const useVoice = (onResult) => {
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const supported = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const isSecure = typeof window !== "undefined" && window.isSecureContext;

  const start = async () => {
    if (!supported) {
      toast.error("Reconhecimento de voz não disponível neste navegador. Use Chrome ou Edge no desktop.");
      return;
    }
    if (!isSecure) {
      toast.error("Áudio só funciona em HTTPS.");
      return;
    }
    // Pre-request microphone permission so the browser shows the prompt clearly
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
      }
    } catch (err) {
      toast.error("Permissão de microfone negada. Libere nas configurações do navegador (cadeado ao lado da URL).");
      return;
    }
    try {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      const rec = new SR();
      rec.lang = "pt-BR";
      rec.interimResults = false;
      rec.continuous = false;
      rec.maxAlternatives = 1;
      rec.onresult = (e) => {
        const transcript = Array.from(e.results).map((r) => r[0].transcript).join(" ");
        if (transcript) {
          onResult(transcript);
          toast.success(`Capturado: "${transcript.slice(0, 60)}"`);
        }
        setListening(false);
      };
      rec.onerror = (e) => {
        setListening(false);
        const reasons = {
          "not-allowed":   "Permissão de microfone negada.",
          "no-speech":     "Nenhuma fala detectada — tente falar mais alto.",
          "audio-capture": "Microfone não detectado.",
          "network":       "Sem internet para processar a voz.",
          "aborted":       "Captura cancelada.",
        };
        toast.error(reasons[e.error] || `Erro de áudio: ${e.error}`);
      };
      rec.onend = () => setListening(false);
      rec.start();
      recRef.current = rec;
      setListening(true);
    } catch (err) {
      setListening(false);
      toast.error(`Falha ao iniciar áudio: ${err.message || err}`);
    }
  };

  const stop = () => { try { recRef.current?.stop(); } catch { /* */ } setListening(false); };
  return { listening, start, stop, supported: !!supported };
};

const LancamentoModal = ({ open, onOpenChange, mode = "create", tipo: tipoFixed, item, onSaved, defaultMonth }) => {
  const { data, addLancamento, updateLancamento, updateLancamentoSerie, extendLancamentoSerie, truncateLancamentoSerie } = useAppData();
  const allCategories = getCategories(data.customCategories || []);
  const initialTipo = tipoFixed || (item ? "variavel" : "variavel");
  const [tipo, setTipo] = useState(initialTipo);
  const [form, setForm] = useState(item || buildEmpty(initialTipo));
  // "single" = update only this entry; "all" = update all entries in the same series
  const [seriesScope, setSeriesScope] = useState("single");
  const [repetirAteEdit, setRepetirAteEdit] = useState("");

  useEffect(() => {
    if (open) {
      const t = tipoFixed || (item ? (item.cat ? "variavel" : "entrada") : "variavel");
      setTipo(t);
      // Never carry `repetir: true` into edit mode — it's a create-time flag only
      setForm(item ? { ...item, repetir: false } : buildEmpty(t));
      setSeriesScope("single");

      // Pre-fill the series end month when editing a recurring item
      if (item?.seriesId) {
        const keyMap = { fixo: "gastosFixos", variavel: "gastosVariaveis", entrada: "entradas" };
        const key = keyMap[t] || "gastosFixos";
        const seriesItems = (data[key] || []).filter((x) => x.seriesId === item.seriesId);
        const maxData = seriesItems.reduce((max, x) => ((x.data || "") > max ? (x.data || "") : max), "");
        setRepetirAteEdit(maxData.slice(0, 7) || "");
      } else {
        setRepetirAteEdit("");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item, tipoFixed, defaultMonth]);

  const onTipoChange = (t) => {
    setTipo(t);
    setForm((f) => ({ ...f, cat: t === "entrada" ? "salario" : (f.cat || "mercado") }));
  };

  /* voice fills the description */
  const voice = useVoice((text) => setForm((f) => ({ ...f, desc: f.desc ? `${f.desc} ${text}` : text })));

  const submit = (e) => {
    e?.preventDefault();
    if (!form.desc?.trim()) return toast.error("Informe a descrição.");
    const valor = Number(String(form.valor).replace(",", "."));
    if (!valor || valor <= 0) return toast.error("Informe um valor válido.");
    const payload = { ...form, valor };

    if (mode === "edit" && item) {
      // In edit mode we NEVER create new entries — only update existing ones
      updateLancamento(tipo, item.id, payload);

      // If item belongs to a series and user chose to propagate to whole series
      if (seriesScope === "all" && item.seriesId) {
        const { desc, valor: v, cat, pagamento, nota, data: newData } = payload;
        const newDay = newData ? newData.slice(8, 10) : null;
        updateLancamentoSerie(tipo, item.seriesId, item.id, { desc, valor: v, cat, pagamento, nota }, newDay);

        // Resize series if the user changed the end month
        if (repetirAteEdit) {
          const keyMap = { fixo: "gastosFixos", variavel: "gastosVariaveis", entrada: "entradas" };
          const key = keyMap[tipo];
          const seriesItems = (data[key] || []).filter((x) => x.seriesId === item.seriesId);
          const currentEndYM = seriesItems.reduce(
            (max, x) => ((x.data || "") > max ? (x.data || "").slice(0, 7) : max),
            ""
          );
          if (currentEndYM && repetirAteEdit > currentEndYM) {
            extendLancamentoSerie(tipo, item.seriesId, currentEndYM, repetirAteEdit, { ...payload, tipoOrigin: tipo[0] });
          } else if (currentEndYM && repetirAteEdit < currentEndYM) {
            truncateLancamentoSerie(tipo, item.seriesId, repetirAteEdit);
          }
        }

        toast.success("Série atualizada!");
      } else {
        const oldYM = (item.data || "").slice(0, 7);
        const newYM = (payload.data || "").slice(0, 7);
        if (oldYM && newYM && oldYM !== newYM) {
          toast.success(`Atualizado e movido para ${newYM}`);
        } else {
          toast.success("Lançamento atualizado!");
        }
      }
    } else {
      addLancamento(tipo, payload);
      toast.success("Lançamento criado!");
    }
    onSaved?.(payload);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lancamento-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {mode === "edit" ? "Editar lançamento" : "Novo lançamento"}
          </DialogTitle>
          <DialogDescription>Preencha os campos ou use o microfone para descrever por voz.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {/* Tipo */}
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Tipo</label>
            <div className="mt-1.5 grid grid-cols-3 gap-2">
              {TIPOS.map((t) => {
                const active = tipo === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    disabled={mode === "edit" && tipoFixed}
                    onClick={() => onTipoChange(t.id)}
                    className={`px-3 py-2 rounded-xl text-sm font-semibold border transition ${
                      active ? t.on : "border-slate-700 text-slate-400 hover:border-slate-600"
                    }`}
                    data-testid={`tipo-${t.id}`}
                  >{t.label}</button>
                );
              })}
            </div>
          </div>

          {/* Descrição + voice */}
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Descrição</label>
            <div className="mt-1.5 flex gap-2">
              <Input
                value={form.desc}
                onChange={(e) => setForm({ ...form, desc: e.target.value })}
                placeholder="Ex: Mercado Dia, Salário, Uber..."
                data-testid="input-desc"
              />
              {voice.supported && (
                <button
                  type="button"
                  onClick={voice.listening ? voice.stop : voice.start}
                  className={`h-10 w-10 grid place-items-center rounded-lg border transition ${
                    voice.listening
                      ? "bg-rose-500/20 border-rose-500/50 text-rose-300 animate-pulse"
                      : "border-slate-700 text-slate-300 hover:border-emerald-500/40 hover:text-emerald-300"
                  }`}
                  data-testid="voice-btn"
                  title={voice.listening ? "Parar" : "Falar"}
                >
                  {voice.listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </button>
              )}
            </div>
            {voice.listening && <div className="mt-1 text-[11px] text-rose-300">Ouvindo... fale agora.</div>}
          </div>

          {/* Valor + Data */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Valor (R$)</label>
              <Input
                type="text" inputMode="decimal"
                value={form.valor}
                onChange={(e) => setForm({ ...form, valor: e.target.value })}
                placeholder="0,00"
                data-testid="input-valor"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Data</label>
              <Input
                type="date"
                value={form.data}
                onChange={(e) => setForm({ ...form, data: e.target.value })}
                data-testid="input-data"
              />
            </div>
          </div>

          {/* Pagamento + Categoria */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Forma de pagamento</label>
              <select
                value={form.pagamento}
                onChange={(e) => setForm({ ...form, pagamento: e.target.value })}
                className="mt-1.5 w-full h-10 rounded-md bg-slate-900 border border-slate-700 px-3 text-sm focus:outline-none focus:border-emerald-500/40"
                data-testid="input-pagamento"
              >
                {PAGAMENTOS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Categoria</label>
              <select
                value={form.cat || (tipo === "entrada" ? "salario" : "mercado")}
                onChange={(e) => setForm({ ...form, cat: e.target.value })}
                className="mt-1.5 w-full h-10 rounded-md bg-slate-900 border border-slate-700 px-3 text-sm focus:outline-none focus:border-emerald-500/40"
                data-testid="input-categoria"
              >
                {tipo === "entrada"
                  ? INCOME_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)
                  : allCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)
                }
              </select>
            </div>
          </div>

          {/* Pago */}
          <div className="flex flex-wrap items-center gap-4">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.pago}
                onChange={(e) => setForm({ ...form, pago: e.target.checked })}
                className="h-4 w-4 accent-emerald-500"
                data-testid="input-pago"
              />
              <span className="text-slate-300">Já pago / recebido</span>
            </label>

            {/* Repetir — only shown in create mode */}
            {mode !== "edit" && (
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.repetir}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      const baseYear = parseInt((form.data || today()).slice(0, 4), 10);
                      setForm((f) => ({ ...f, repetir: true, repetirAte: f.repetirAte || `${baseYear}-12` }));
                    } else {
                      setForm((f) => ({ ...f, repetir: false }));
                    }
                  }}
                  className="h-4 w-4 accent-emerald-500"
                  data-testid="input-repetir"
                />
                <span className="text-slate-300">Repetir nos próximos meses</span>
              </label>
            )}
          </div>

          {/* Series scope — only shown when editing an item that belongs to a series */}
          {mode === "edit" && item?.seriesId && (
            <div className="rounded-xl border border-blue-500/30 bg-blue-500/10 p-3 space-y-2">
              <div className="text-xs font-semibold text-blue-300 uppercase tracking-wider">
                Este lançamento faz parte de uma série recorrente
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="seriesScope" value="single" checked={seriesScope === "single"}
                  onChange={() => setSeriesScope("single")} className="accent-emerald-500" />
                <span className="text-slate-300">Atualizar apenas este mês</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="seriesScope" value="all" checked={seriesScope === "all"}
                  onChange={() => setSeriesScope("all")} className="accent-emerald-500" />
                <span className="text-slate-300">Atualizar todos os meses da série</span>
              </label>

              {seriesScope === "all" && (
                <div className="pt-1">
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Repetir até qual mês?
                  </label>
                  <select
                    value={repetirAteEdit}
                    onChange={(e) => setRepetirAteEdit(e.target.value)}
                    className="mt-1.5 w-full h-10 rounded-md bg-slate-900 border border-slate-700 px-3 text-sm focus:outline-none focus:border-emerald-500/40"
                    data-testid="input-repetir-ate-edit"
                  >
                    {(() => {
                      const baseYear = parseInt((item.data || today()).slice(0, 4), 10);
                      const baseM   = parseInt((item.data || today()).slice(5, 7),  10);
                      const opts = [];
                      for (let i = 0; i <= 35; i++) {
                        const totalM = baseM + i - 1;
                        const y = baseYear + Math.floor(totalM / 12);
                        const m = (totalM % 12) + 1;
                        const val = `${y}-${String(m).padStart(2, "0")}`;
                        opts.push(
                          <option key={val} value={val}>
                            {MES_NOMES_MODAL[m - 1]} {y}
                          </option>
                        );
                      }
                      return opts;
                    })()}
                  </select>
                  <p className="mt-1 text-[11px] text-slate-500">
                    Mês pré-selecionado = término atual da série. Altere para estender ou encurtar.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Repetir até — only in create mode with repetir checked */}
          {mode !== "edit" && form.repetir && (
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Repetir até qual mês?</label>
              <select
                value={form.repetirAte || ""}
                onChange={(e) => setForm({ ...form, repetirAte: e.target.value })}
                className="mt-1.5 w-full h-10 rounded-md bg-slate-900 border border-slate-700 px-3 text-sm focus:outline-none focus:border-emerald-500/40"
                data-testid="input-repetir-ate"
              >
                {(() => {
                  const baseYear = parseInt((form.data || today()).slice(0, 4), 10);
                  const baseM = parseInt((form.data || today()).slice(5, 7), 10);
                  const opts = [];
                  for (let i = 1; i <= 11; i++) {
                    const totalM = baseM + i;
                    const y = baseYear + Math.floor((totalM - 1) / 12);
                    const m = ((totalM - 1) % 12) + 1;
                    const val = `${y}-${String(m).padStart(2, "0")}`;
                    opts.push(<option key={val} value={val}>{MES_NOMES_MODAL[m - 1]} {y}</option>);
                  }
                  return opts;
                })()}
              </select>
            </div>
          )}

          {/* Nota opcional */}
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Observação / Nota <span className="normal-case font-normal text-slate-500">(opcional)</span></label>
            <textarea
              value={form.nota || ""}
              onChange={(e) => setForm({ ...form, nota: e.target.value })}
              placeholder="Detalhes adicionais sobre este lançamento..."
              rows={2}
              className="mt-1.5 w-full rounded-md bg-slate-900 border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500/40 resize-none text-white placeholder:text-slate-500"
              data-testid="input-nota"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1" data-testid="btn-cancelar">
              <X className="h-4 w-4 mr-1.5" /> Cancelar
            </Button>
            <Button type="submit" className="flex-1 monyx-btn-primary text-white" data-testid="btn-salvar">
              <Save className="h-4 w-4 mr-1.5" /> {mode === "edit" ? "Atualizar" : "Salvar lançamento"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LancamentoModal;
